import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';

import { useApi } from '@/providers/ApiContext';
import {
  createOrganization,
  getOrganization,
  updateOrganization,
  type OrganizationCreateRequest,
  type OrganizationUpdateRequest,
} from '@/actions/org';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import GeneralFields from './GeneralFields';
import EndpointsFields from './EndpointsFields';
import AuthFields from './AuthFields';
import ModelsFields from './ModelsFields';

const emptyCreateValues: OrganizationCreateRequest = {
  name: '',
  email: '',
  customer_domain: '',
  tenant_domain: '',
  api_endpoint: '',
  on_prem: false,
};

function OrganizationForm(): React.JSX.Element {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const api = useApi();

  const organization_id = sp.get('organization_id');
  const isEdit = Boolean(organization_id);

  const [loading_org, setLoadingOrg] = useState<boolean>(isEdit);
  const [loading, setLoading] = useState<boolean>(false);
  const [pristine, setPristine] = useState<boolean>(true);
  const [api_error, setApiError] = useState<string | null>(null);

  const [values, setValues] = useState<OrganizationUpdateRequest>(
    emptyCreateValues,
  );
  const [errors, setErrors] = useState<
    Partial<Record<keyof OrganizationUpdateRequest, string>>
  >({});

  useEffect(() => {
    if (!api || !organization_id) return;

    setLoadingOrg(true);
    getOrganization(api, organization_id)
      .then((org) => {
        const {
          id: _id,
          entity_id: _entity_id,
          created_at: _created_at,
          updated_at: _updated_at,
          vdb_key: _vdb_key,
          client_jwt: _client_jwt,
          client_jwt_exp: _client_jwt_exp,
          client_key: _client_key,
          ...editable
        } = org;
        setValues(editable);
        setPristine(true);
      })
      .catch((err) => {
        const error = err as Error;
        setApiError(
          error.message ||
            'Unable to fetch organization for editing. This could be due to network issues or server problems.',
        );
      })
      .finally(() => {
        setLoadingOrg(false);
      });
  }, [api, organization_id]);

  const setValue = useCallback(
    <K extends keyof OrganizationUpdateRequest>(
      key: K,
      value: OrganizationUpdateRequest[K],
    ) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const validate = useCallback(() => {
    const nextErrors: Partial<Record<keyof OrganizationUpdateRequest, string>> =
      {};

    const requireString = (key: keyof OrganizationCreateRequest, label: string) => {
      const v = (values as any)[key];
      if (typeof v !== 'string' || !v.trim()) {
        (nextErrors as any)[key] = `${label} is required`;
      }
    };

    requireString('name', 'Name');
    requireString('email', 'Email');
    requireString('customer_domain', 'Customer Domain');
    requireString('tenant_domain', 'Tenant Domain');
    requireString('api_endpoint', 'API Endpoint');

    const urlKeys: (keyof OrganizationUpdateRequest)[] = [
      'authority',
      'redirect_uri',
      'post_logout_redirect_uri',
      'introspection_endpoint',
      'llm_endpoint',
      'vllm_endpoint',
      'embedding_endpoint',
      'vdb_endpoint',
      'api_endpoint',
      'te_endpoint',
    ];
    urlKeys.forEach((key) => {
      const v = values[key];
      if (!v) return;
      if (typeof v !== 'string') return;
      try {
        // eslint-disable-next-line no-new
        new URL(v);
      } catch {
        nextErrors[key] = 'Must be a valid URL';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [values]);

  const title = useMemo(() => {
    if (isEdit) return 'Edit Organization';
    return 'Create Organization';
  }, [isEdit]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!api) return;

    if (!validate()) return;

    try {
      setLoading(true);
      if (organization_id) {
        await updateOrganization(api, organization_id, values);
      } else {
        await createOrganization(api, values as OrganizationCreateRequest);
      }
      navigate('/organizations');
    } catch (err) {
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to save organization. This could be due to network issues, server problems, or invalid data.',
      );
    } finally {
      setLoading(false);
    }
  }, [api, navigate, organization_id, validate, values]);

  if (loading_org) {
    return <LoadingMask />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
            <Toolbar sx={{ minHeight: '55px !important' }}>
              <Stack spacing={2} direction='row' alignItems='center'>
                <IconButton onClick={() => navigate(-1)} disabled={loading}>
                  <ArrowBack />
                </IconButton>
                <Typography variant='h5'>{title}</Typography>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>

        <Grid size={12}>
          <Box padding='15px' sx={{ maxWidth: 900 }}>
            <GeneralFields
              values={values}
              setValue={setValue}
              setChanged={() => setPristine(false)}
              errors={errors}
            />

            <EndpointsFields
              values={values}
              setValue={setValue}
              setChanged={() => setPristine(false)}
              errors={errors}
            />

            <AuthFields
              values={values}
              setValue={setValue}
              setChanged={() => setPristine(false)}
              errors={errors}
            />

            <ModelsFields
              values={values}
              setValue={setValue}
              setChanged={() => setPristine(false)}
              errors={errors}
            />

            <Divider sx={{ my: 3 }} />

            <Stack direction='row' spacing={2}>
              <LoadingButton
                loading={loading}
                variant='contained'
                onClick={handleSubmit}
                disabled={loading || pristine}
              >
                Save
              </LoadingButton>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default OrganizationForm;

